/**
 * TagUsageModal — the global tag explorer.
 *
 * Shows everything in the portal carrying one tag, grouped by record type,
 * with a human-readable reference per record and an "Open" link that lands on
 * the right module pre-filtered to the tag.
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMsal } from '@azure/msal-react';
import TagChip from './TagChip';
import { fetchTagEntities } from './tagApi';
import { buildTagEntityUrl } from './tagEntityLinks';

const TagUsageModal = ({ tagId, onClose }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (tagId == null) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchTagEntities(instance, accounts, tagId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.message || 'Failed to load tag usage'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [instance, accounts, tagId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (tagId == null) return null;

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10050,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white', borderRadius: '10px', width: 'min(680px, 94vw)',
          maxHeight: '82vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.6rem',
          padding: '0.9rem 1.1rem', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb',
        }}>
          <span style={{ fontSize: '1.1rem' }}>🔎</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: '#111827', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              {data?.tag ? <TagChip tag={data.tag} /> : 'Tag'}
              <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.85rem' }}>
                {data ? `${data.total} tagged record${data.total === 1 ? '' : 's'} across the portal` : ''}
              </span>
            </div>
            {data?.tag?.category_name && (
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 2 }}>
                Category: {data.tag.category_name}
                {data.tag.category_scope === 'project' ? ' (project-scoped)' : ''}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{ border: 'none', background: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', padding: '0.9rem 1.1rem', flex: 1 }}>
          {loading && <div style={{ color: '#6b7280', padding: '1rem 0' }}>Loading tag usage…</div>}
          {error && <div style={{ color: '#dc2626', padding: '1rem 0' }}>{error}</div>}
          {!loading && !error && data && data.groups.length === 0 && (
            <div style={{ color: '#6b7280', padding: '1rem 0' }}>
              This tag isn't applied to anything yet.
            </div>
          )}
          {!loading && !error && data && data.groups.map((group) => (
            <div key={group.entityType} style={{ marginBottom: '1.1rem' }}>
              <div style={{
                fontSize: '0.8rem', fontWeight: 700, color: '#374151',
                textTransform: 'uppercase', letterSpacing: '0.03em',
                padding: '0.3rem 0', borderBottom: '2px solid #e5e7eb', marginBottom: '0.35rem',
              }}>
                {group.module} — {group.label}
                <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>({group.count})</span>
              </div>
              {group.items.map((item) => {
                const url = buildTagEntityUrl(group.entityType, item.projectId, data.tag?.id);
                return (
                  <div
                    key={`${group.entityType}-${item.entityId}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.6rem',
                      padding: '0.35rem 0.2rem', borderBottom: '1px solid #f3f4f6', fontSize: '0.85rem',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{
                        color: item.missing || item.deleted ? '#9ca3af' : '#111827',
                        textDecoration: item.missing || item.deleted ? 'line-through' : 'none',
                        overflowWrap: 'anywhere',
                      }}>
                        {item.reference}
                      </span>
                      {item.deleted && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#b91c1c' }}>(deleted)</span>}
                      {item.missing && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#b91c1c' }}>(row removed)</span>}
                    </div>
                    <span style={{ color: '#6b7280', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                      {item.jobNumber || (item.projectName ? item.projectName : '')}
                    </span>
                    {url && !item.missing && (
                      <a
                        href={url}
                        style={{
                          color: '#2563eb', textDecoration: 'none', fontSize: '0.78rem',
                          whiteSpace: 'nowrap', fontWeight: 600,
                        }}
                        title="Open this tracker filtered to the tag"
                      >
                        Open ↗
                      </a>
                    )}
                  </div>
                );
              })}
              {group.truncated && (
                <div style={{ color: '#9ca3af', fontSize: '0.75rem', padding: '0.3rem 0' }}>
                  Showing the first {group.items.length} of {group.count}.
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TagUsageModal;
